import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useTrans } from '../../Store/System/LocalizationStore';
import { Button } from '../../Components/Button';
import { useRootStore } from '../../Store/useRootStore';
import { useDeleteAthlete } from '../../Hooks/useAthleteMutations';
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { useAuth0 } from '@auth0/auth0-react';
import { getUserAuthCapabilities } from './authUtils';
import { Theme } from '../../Theme/Theme';
import { ChangeEmailModal } from './ChangeEmailModal';
import { ChangePasswordModal } from './ChangePasswordModal';
import { PersonalInfoSection } from './PersonalInfoSection';
import { SecuritySection } from './SecuritySection';
import { TrackersSection } from './TrackersSection';
import { SettingsSection } from './SettingsSection';
import { SupportSection } from './SupportSection';
import { ActiveProgramsSection } from './ActiveProgramsSection';
import { FitnessSettingsSection } from './FitnessSettingsSection';
import { DepartmentSection } from './DepartmentSection';
import { DietSection } from './DietSection';

type AccountSection = 'profile' | 'programs' | 'security' | 'trackers' | 'settings' | 'support' | 'fitness' | 'diet' | 'department';

const PageContainer = styled.div`
  display: flex;
  min-height: calc(100vh - 64px);
  background-color: ${Theme.Colors.background};
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const Sidebar = styled.nav`
  width: 260px;
  background: white;
  border-right: 1px solid ${Theme.Colors.lighterGrey};
  padding: 24px 0;
  flex-shrink: 0;
  position: sticky;
  top: 80px;
  height: fit-content;
  max-height: calc(100vh - 80px);
  overflow-y: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileSectionSelector = styled.div`
  display: none;
  padding: 16px;
  background: white;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 8px;
  font-size: 16px;
  background: white;
  cursor: pointer;
`;

const SidebarSection = styled.div`
  margin-bottom: 8px;
`;

const SidebarSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${Theme.Colors.warmGrey};
  padding: 8px 24px;
  margin-top: 16px;

  &:first-of-type {
    margin-top: 0;
  }
`;

const SidebarItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  border: none;
  background: ${(props) => (props.$active ? `${Theme.Colors.primary}10` : 'transparent')};
  color: ${(props) => (props.$active ? Theme.Colors.primary : Theme.Colors.textColor)};
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? '600' : '400')};
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
  border-left: 3px solid ${(props) => (props.$active ? Theme.Colors.primary : 'transparent')};

  &:hover {
    background: ${(props) => (props.$active ? `${Theme.Colors.primary}10` : Theme.Colors.lightestGrey)};
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const ContentArea = styled.main`
  flex: 1;
  padding: 32px;
  max-width: 900px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentHeader = styled.div`
  margin-left: 24px;
  margin-bottom: 24px;
`;

const ContentTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const ContentDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

const ProfileIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const SecurityIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const TrackerIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 2v4M8 2v4M2 12h20" />
  </svg>
);

const SettingsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const SupportIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const LogoutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const ProgramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const FitnessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6.5 6.5m-3.5 0a3.5 3.5 0 1 0 7 0a3.5 3.5 0 1 0 -7 0" />
    <path d="M2.5 21v-2a4 4 0 0 1 4-4h.5" />
    <path d="M14 3.5c0 2 1.5 3.5 3.5 3.5" />
    <path d="M18.5 15.5m-2.5 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 1 0 -5 0" />
    <path d="M18.5 18l-1.5 3" />
    <path d="M12 12l6 -6" />
    <path d="M18 6l2 -2" />
    <path d="M18 6l-2 2" />
  </svg>
);

const DietIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 10a6 6 0 0 0-6 6c0 3.31 2.69 6 6 6s6-2.69 6-6a6 6 0 0 0-6-6z" />
    <path d="M12 2v4" />
    <path d="M10 6c0-1.1.9-2 2-2s2 .9 2 2c0 0-1.5.5-2 2-.5-1.5-2-2-2-2z" />
  </svg>
);

const DepartmentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export const AccountScreen: React.FC = observer(() => {
  const { t } = useTrans();
  const { authentication } = useRootStore();
  const { user: auth0User, logout } = useAuth0();
  const location = useLocation();

  const [activeSection, setActiveSection] = useState<AccountSection>('profile');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteDisabled, setDeleteDisabled] = useState(true);
  const [changeEmailModalOpen, setChangeEmailModalOpen] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [passwordMode, setPasswordMode] = useState<'change' | 'set'>('change');

  const capabilities = useMemo(() => getUserAuthCapabilities(auth0User), [auth0User]);
  const athlete = authentication.athlete;
  const deleteAthlete = useDeleteAthlete();

  // Handle URL hash for direct linking to sections
  useEffect(() => {
    const hash = location.hash.replace('#', '') as AccountSection;
    if (['profile', 'programs', 'security', 'trackers', 'settings', 'support', 'fitness', 'diet', 'department'].includes(hash)) {
      setActiveSection(hash);
    }
  }, [location.hash]);

  useEffect(() => {
    setDeleteDisabled(true);
    const timeout = deleteModalOpen ? setTimeout(() => setDeleteDisabled(false), 500) : undefined;
    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [deleteModalOpen]);

  const handleSectionChange = useCallback((section: AccountSection) => {
    setActiveSection(section);
    window.history.replaceState(null, '', `#${section}`);
  }, []);

  const handleMobileSectionChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    handleSectionChange(e.target.value as AccountSection);
  }, [handleSectionChange]);

  const handleLogout = useCallback(() => {
    logout({
      logoutParams: { returnTo: window.location.origin + '/auth' },
    });
  }, [logout]);

  const handleShowDeleteAccount = useCallback(() => setDeleteModalOpen(true), []);
  const handleToggleDeleteModal = useCallback(() => setDeleteModalOpen((p) => !p), []);

  const handleShowChangeEmail = useCallback(() => setChangeEmailModalOpen(true), []);
  const handleCloseChangeEmail = useCallback(() => setChangeEmailModalOpen(false), []);

  const handleShowChangePassword = useCallback((mode: 'change' | 'set') => {
    setPasswordMode(mode);
    setChangePasswordModalOpen(true);
  }, []);
  const handleCloseChangePassword = useCallback(() => setChangePasswordModalOpen(false), []);

  const handleDeleteAccount = useCallback(() => {
    if (athlete) {
      deleteAthlete
        .mutateAsync()
        .then(() => handleLogout())
        .catch((err) => {
          console.error('Failed to delete account:', err);
          window.alert(
            t('accountScreen.deleteAccountModal.deleteError', {
              defaultValue: 'Failed to delete account. Please try again.',
            }),
          );
          setDeleteModalOpen(false);
        });
    }
  }, [athlete, deleteAthlete, handleLogout, t]);

  const sectionConfig: Record<AccountSection, { title: string; description: string }> = {
    profile: {
      title: t('accountScreen.sections.profile.title', { defaultValue: 'Profile' }),
      description: t('accountScreen.sections.profile.description', { defaultValue: 'Manage your personal information' }),
    },
    programs: {
      title: t('accountScreen.sections.programs.title', { defaultValue: 'Active Programs' }),
      description: t('accountScreen.sections.programs.description', { defaultValue: 'Track your active fitness and mindfulness programs' }),
    },
    security: {
      title: t('accountScreen.sections.security.title', { defaultValue: 'Security' }),
      description: t('accountScreen.sections.security.description', { defaultValue: 'Manage your email, password and security settings' }),
    },
    trackers: {
      title: t('accountScreen.sections.trackers.title', { defaultValue: 'Trackers' }),
      description: t('accountScreen.sections.trackers.description', { defaultValue: 'Connect and manage your fitness trackers' }),
    },
    settings: {
      title: t('accountScreen.sections.settings.title', { defaultValue: 'Settings' }),
      description: t('accountScreen.sections.settings.description', { defaultValue: 'Customize your app preferences' }),
    },
    support: {
      title: t('accountScreen.sections.support.title', { defaultValue: 'Help & Support' }),
      description: t('accountScreen.sections.support.description', { defaultValue: 'Get help and contact us' }),
    },
    fitness: {
      title: t('accountScreen.sections.fitness.title', { defaultValue: 'Fitness Settings' }),
      description: t('accountScreen.sections.fitness.description', { defaultValue: 'Configure your fitness level and equipment' }),
    },
    diet: {
      title: t('accountScreen.sections.diet.title', { defaultValue: 'Diet Preferences' }),
      description: t('accountScreen.sections.diet.description', { defaultValue: 'Set your dietary preferences for personalized recommendations' }),
    },
    department: {
      title: t('accountScreen.sections.department.title', { defaultValue: 'Department' }),
      description: t('accountScreen.sections.department.description', { defaultValue: 'Select your department within your organization' }),
    },
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'profile':
        return <PersonalInfoSection />;

      case 'programs':
        return <ActiveProgramsSection />;

      case 'security':
        return (
          <SecuritySection
            canChangeEmail={capabilities.canChangeEmail}
            canChangePassword={capabilities.canChangePassword}
            canManageMfa={capabilities.canManageMfa}
            connectionType={capabilities.connectionType}
            connection={capabilities.connection}
            email={auth0User?.email}
            emailVerified={auth0User?.email_verified}
            onChangeEmail={handleShowChangeEmail}
            onChangePassword={() => handleShowChangePassword('change')}
            onSetPassword={() => handleShowChangePassword('set')}
          />
        );

      case 'trackers':
        return <TrackersSection />;

      case 'settings':
        return <SettingsSection />;

      case 'support':
        return <SupportSection />;

      case 'fitness':
        return <FitnessSettingsSection />;

      case 'diet':
        return <DietSection />;

      case 'department':
        return <DepartmentSection />;

      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <PageContainer>
        <Sidebar>
          <SidebarSectionTitle>{t('accountScreen.nav.account', { defaultValue: 'Account' })}</SidebarSectionTitle>
          <SidebarSection>
            <SidebarItem $active={activeSection === 'profile'} onClick={() => handleSectionChange('profile')}>
              <ProfileIcon />
              {t('accountScreen.nav.profile', { defaultValue: 'Profile' })}
            </SidebarItem>
            <SidebarItem $active={activeSection === 'security'} onClick={() => handleSectionChange('security')}>
              <SecurityIcon />
              {t('accountScreen.nav.security', { defaultValue: 'Security' })}
            </SidebarItem>
          </SidebarSection>

          <SidebarSectionTitle>{t('accountScreen.nav.healthFitness', { defaultValue: 'Health & Fitness' })}</SidebarSectionTitle>
          <SidebarSection>
            <SidebarItem $active={activeSection === 'programs'} onClick={() => handleSectionChange('programs')}>
              <ProgramIcon />
              {t('accountScreen.nav.programs', { defaultValue: 'Programs' })}
            </SidebarItem>
            <SidebarItem $active={activeSection === 'trackers'} onClick={() => handleSectionChange('trackers')}>
              <TrackerIcon />
              {t('accountScreen.nav.trackers', { defaultValue: 'Trackers' })}
            </SidebarItem>
            <SidebarItem $active={activeSection === 'fitness'} onClick={() => handleSectionChange('fitness')}>
              <FitnessIcon />
              {t('accountScreen.nav.fitness', { defaultValue: 'Fitness Settings' })}
            </SidebarItem>
            <SidebarItem $active={activeSection === 'diet'} onClick={() => handleSectionChange('diet')}>
              <DietIcon />
              {t('accountScreen.nav.diet', { defaultValue: 'Diet' })}
            </SidebarItem>
          </SidebarSection>

          <SidebarSectionTitle>{t('accountScreen.nav.preferences', { defaultValue: 'Preferences' })}</SidebarSectionTitle>
          <SidebarSection>
            <SidebarItem $active={activeSection === 'settings'} onClick={() => handleSectionChange('settings')}>
              <SettingsIcon />
              {t('accountScreen.nav.settings', { defaultValue: 'Settings' })}
            </SidebarItem>
            <SidebarItem $active={activeSection === 'department'} onClick={() => handleSectionChange('department')}>
              <DepartmentIcon />
              {t('accountScreen.nav.department', { defaultValue: 'Department' })}
            </SidebarItem>
          </SidebarSection>

          <SidebarSectionTitle>{t('accountScreen.nav.more', { defaultValue: 'More' })}</SidebarSectionTitle>
          <SidebarSection>
            <SidebarItem $active={activeSection === 'support'} onClick={() => handleSectionChange('support')}>
              <SupportIcon />
              {t('accountScreen.nav.support', { defaultValue: 'Help & Support' })}
            </SidebarItem>
            <SidebarItem onClick={handleLogout}>
              <LogoutIcon />
              {t('accountScreen.logoutButton', { defaultValue: 'Logout' })}
            </SidebarItem>
          </SidebarSection>
        </Sidebar>

        <ContentArea>
          <MobileSectionSelector>
            <MobileSelect value={activeSection} onChange={handleMobileSectionChange}>
              <option value="profile">{t('accountScreen.nav.profile', { defaultValue: 'Profile' })}</option>
              <option value="programs">{t('accountScreen.nav.programs', { defaultValue: 'Programs' })}</option>
              <option value="security">{t('accountScreen.nav.security', { defaultValue: 'Security' })}</option>
              <option value="trackers">{t('accountScreen.nav.trackers', { defaultValue: 'Trackers' })}</option>
              <option value="fitness">{t('accountScreen.nav.fitness', { defaultValue: 'Fitness Settings' })}</option>
              <option value="diet">{t('accountScreen.nav.diet', { defaultValue: 'Diet' })}</option>
              <option value="settings">{t('accountScreen.nav.settings', { defaultValue: 'Settings' })}</option>
              <option value="department">{t('accountScreen.nav.department', { defaultValue: 'Department' })}</option>
              <option value="support">{t('accountScreen.nav.support', { defaultValue: 'Help & Support' })}</option>
            </MobileSelect>
          </MobileSectionSelector>

          <ContentHeader>
            <ContentTitle>{sectionConfig[activeSection].title}</ContentTitle>
            <ContentDescription>{sectionConfig[activeSection].description}</ContentDescription>
          </ContentHeader>

          {renderSectionContent()}
        </ContentArea>
      </PageContainer>

      {/* Delete Account Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={handleToggleDeleteModal} centered>
        <ModalHeader toggle={handleToggleDeleteModal}>
          {t('accountScreen.deleteAccountModal.title', { defaultValue: 'Delete Account' })}
        </ModalHeader>
        <ModalBody>
          {t('accountScreen.deleteAccountModal.description', {
            defaultValue:
              'You will lose access to your account and your subscription. We will be unable to restore any of your data.',
          })}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" onClick={handleToggleDeleteModal}>
            {t('globals.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button color="danger" size="sm" onClick={handleDeleteAccount} disabled={deleteDisabled}>
            {t('accountScreen.deleteAccountModal.deleteButton', { defaultValue: 'Delete Account' })}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Change Email Modal */}
      <ChangeEmailModal isOpen={changeEmailModalOpen} onClose={handleCloseChangeEmail} />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={changePasswordModalOpen}
        onClose={handleCloseChangePassword}
        mode={passwordMode}
      />
    </React.Fragment>
  );
});
